import { ChangeDetectionStrategy, Component, Input, ViewChild, ElementRef, OnInit, Optional } from "@angular/core";
import { DynamicDialogConfig } from "primeng/dynamicdialog";
import { Observable } from "rxjs";
import { ImageDto } from "src/app/models/image-dto";

@Component({
    selector: 'app-archive-image-gallery',
    templateUrl: './archive-image-gallery.component.html',
    styleUrls: ['./archive-image-gallery.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArchiveImageGalleryComponent implements OnInit {
    @Input() images: ImageDto[] = [];
    @Input() thumbnails: ImageDto[] = [];
    @Input() showZoom = false;
    @Input() showActivePage = false;
    @Input() currentLanguage!: Observable<string>;

    activeIndex = 0;
    originalImageHeight: number | undefined;
    originalImageWidth: number | undefined;
    displayCustom = false;
    fullScreen = false;
    zoomFactor = 1;
    zoomStep = 0.1;
    loadingImage = false;
    loadingPreviewImage = false;

    @ViewChild('previewImage') previewImage!: ElementRef<HTMLImageElement>;
    @ViewChild('scroller') scroller!: ElementRef<HTMLDivElement>;

    constructor(@Optional() public config?: DynamicDialogConfig) { }

ngOnInit(): void {
    if (this.config?.data) {
        this.images = this.config.data.images || this.images;
        this.showZoom = false;
        this.showActivePage = this.config.data.showActivePage ?? this.showActivePage;
        
        if (!this.config.data.embedded) {
            this.displayCustom = true;
        }
    }
    
    this.originalImageHeight = undefined;
    this.originalImageWidth = undefined;
    
    // If no thumbnails provided, use the full images
    if (!this.thumbnails || this.thumbnails.length === 0) {
        this.thumbnails = this.images;
    }
}

    imageClick() {
        this.displayCustom = true;
    }

    firstImage() {
        this.activeIndex = 0;
        this.loadingImage = true;
    }

    lastImage() {
        if (this.images.length > 0) {
            this.activeIndex = this.images.length - 1;
            this.loadingImage = true;
        }

    }

    prevImage() {
        if (this.activeIndex > 0) {
            this.activeIndex--;
            this.loadingImage = true;
        }
    }

    nextImage() {
        if (this.activeIndex < this.images.length) {
            this.activeIndex++;
            this.loadingImage = true;
        }
    }

    imageLoad() {
        this.loadingImage = false;
    }

    imageChange() {
        this.renewSize();
        this.calculateFitToWidthFactor();
        this.resizeImage();
        this.loadingPreviewImage = false;
    }

    scrollerTop() {
        this.loadingPreviewImage = true;
        this.scroller.nativeElement.scrollTop = 0;
    }

    resetZoom(): void {
        this.refreshSize();
        if (this.originalImageHeight && this.originalImageWidth) {
            this.previewImage.nativeElement.height = this.originalImageHeight;
            this.previewImage.nativeElement.width = this.originalImageWidth;
        }
    }

    private calculateFitToWidthFactor() {
        if (this.previewImage && this.scroller) {
            this.zoomFactor = this.scroller.nativeElement.clientWidth /
                this.previewImage.nativeElement.naturalWidth;
        }
    }

    private renewSize() {
        if (this.previewImage) {
            this.originalImageHeight = this.previewImage.nativeElement.naturalHeight;
            this.originalImageWidth = this.previewImage.nativeElement.naturalWidth;
        }
    }

    private refreshSize() {
        if (this.originalImageHeight === undefined) {
            this.renewSize();
        }
    }

    zoomIn(): void {
        this.refreshSize();
        if (this.originalImageHeight && this.originalImageWidth) {
            this.zoomFactor += this.zoomStep;
            this.resizeImage();
        }
    }

    zoomOut(): void {
        this.refreshSize();
        if (this.originalImageHeight && this.originalImageWidth) {
            this.zoomFactor -= this.zoomStep;
            this.resizeImage();
        }
    }

    private resizeImage() {
        if (this.originalImageHeight && this.originalImageWidth) {
            this.previewImage.nativeElement.height = this.originalImageHeight * this.zoomFactor;
            this.previewImage.nativeElement.width = this.originalImageWidth * this.zoomFactor;
        }
    }

}